import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import * as tus from 'tus-js-client';
import { toast } from 'react-toastify';
import { ACTION_BLUE } from '../theme/theme';

interface FileUploadProps {
  imageSrc?: React.ReactNode | string;
  uploadText?: string;
  onFilesSelected?: (file: File | null, fileUrl?: string) => void;
  width?: string;
  height?: string;
  backgroundColor?: string;
  direction?: 'ltr' | 'rtl';
  flexDirection?: 'column' | 'row';
  acceptedFileTypes?: string[];
  showFileInfo?: boolean;
  compactView?: boolean;
}

const TusFileUpload: React.FC<FileUploadProps> = ({
  imageSrc = 'default-upload-icon.png',
  uploadText = 'فایل را اینجا بکشید و رها کنید یا کلیک کنید',
  onFilesSelected,
  // Fill the parent by default; callers pass an explicit value when they
  // need a fixed-size zone.
  width = '100%',
  height = '120px',
  backgroundColor = '#F0F0F0',
  direction = 'rtl',
  flexDirection = 'column',
  acceptedFileTypes = ['.ipa'],
  showFileInfo = true,
  //compactView = false,
}) => {
  const [files, setFiles] = useState<
    { file: File; progress: number; url: string | null }[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef<tus.Upload | null>(null);

  const validateFileType = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    return fileExtension && acceptedFileTypes.includes(`.${fileExtension}`);
  };

  const handleFileChange = (incomingFiles: File[]) => {
    const validFiles = incomingFiles.filter((file) => validateFileType(file));

    if (validFiles.length === 0) {
      setError(
        'فایل معتبر انتخاب نشده است. لطفاً فایل‌هایی با فرمت .ipa آپلود کنید.',
      );
      toast.error('لطفاً یک فایل .ipa انتخاب کنید.');
      return;
    }

    setError(null);
    const file = validFiles[0];
    const fileData = { file, progress: 0, url: null };
    setFiles([fileData]);
    setUploading(true);

    const upload = new tus.Upload(file, {
      endpoint: import.meta.env.VITE_TUSD_URL, //'https://upload.staging.sibche.com/files/',
      metadata: {
        filename: file.name,
        filetype: file.type,
      },
      onError: (e) => {
        let errorMessage = 'خطا در آپلود فایل. لطفاً دوباره تلاش کنید.';
        if (e.message.includes('CORS')) {
          errorMessage =
            'خطای دسترسی به سرور آپلود. لطفاً با پشتیبانی تماس بگیرید.';
        }
        setError(errorMessage);
        setUploading(false);
        setFiles([]);
        toast.error(errorMessage);
        console.error('TUS upload error:', e);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
        setFiles((prev) => [
          { ...prev[0], progress: Number(percentage), url: null },
        ]);
      },
      onSuccess: () => {
        setUploading(false);
        const fileUrl = upload.url || null;
        setFiles((prev) => [{ ...prev[0], progress: 100, url: fileUrl }]);
        if (onFilesSelected && fileUrl) {
          onFilesSelected(file, fileUrl);
          toast.success('فایل با موفقیت آپلود شد.');
        } else {
          setError('دریافت URL فایل با مشکل مواجه شد.');
          toast.error('خطا در دریافت URL فایل.');
        }
      },
    });

    uploadRef.current = upload;
    upload.start();
  };

  const handleRemoveFile = () => {
    if (uploadRef.current) {
      uploadRef.current.abort();
      uploadRef.current = null;
    }
    setFiles([]);
    setUploading(false);
    setError(null);
    if (onFilesSelected) {
      onFilesSelected(null);
    }
  };

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const incomingFiles = Array.from(event.dataTransfer.files);
    handleFileChange(incomingFiles);
  };

  const preventDefault = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: width },
        height,
        // Parent forms use `align-items: flex-end`, which would otherwise
        // shrink a full-width box to its content. Only stretch when the
        // caller asked to fill, so fixed-size zones keep their dimensions.
        alignSelf: width === '100%' ? 'stretch' : undefined,
        // padding + border must stay inside the parent's width, otherwise a
        // 100% box overflows by 36px.
        maxWidth: '100%',
        boxSizing: 'border-box',
        borderRadius: '8px',
        display: 'flex',
        flexDirection,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor,
        border: '2px dotted #A6AAB5',
        textAlign: 'center',
        padding: '16px',
        color: '#A6AAB5',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        direction,
      }}
      onClick={handleBoxClick}
      onDrop={handleDrop}
      onDragOver={preventDefault}
      onDragEnter={preventDefault}
      onDragLeave={preventDefault}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={acceptedFileTypes.join(',')}
        onChange={(e) => handleFileChange(Array.from(e.target.files || []))}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {files.length === 0 && !uploading && (
        <>
          {typeof imageSrc === 'string' ? (
            <img
              src={imageSrc}
              alt="upload icon"
              style={{ width: '30px', height: '30px', marginBottom: '8px' }}
            />
          ) : (
            imageSrc
          )}
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', fontFamily: 'IRANYekanXFaNum' }}
          >
            {uploadText}
          </Typography>
        </>
      )}

      {files.map((fileData, index) =>
        showFileInfo ? (
          <Box
            key={index}
            sx={{
              width: '100%',
              mt: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              padding: '1px',
              borderRadius: '4px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', color: '#333' }}
            >
              {fileData.file.name} ({(fileData.file.size / 1024).toFixed(2)} KB)
            </Typography>
            <IconButton
              onClick={handleRemoveFile}
              size="small"
              sx={{
                border: '1px solid #A6AAB5',
                borderRadius: '20%',
                padding: '4px',
                backgroundColor: '#f5f5f5',
                '&:hover': { backgroundColor: '#e0e0e0' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            {uploading && (
              <LinearProgress
                variant="determinate"
                value={fileData.progress}
                sx={{
                  width: '40%',
                  mt: 1,
                  backgroundColor: '#A6AAB5',
                  '& .MuiLinearProgress-bar': { backgroundColor: ACTION_BLUE },
                }}
              />
            )}
          </Box>
        ) : null,
      )}
    </Box>
  );
};

export default TusFileUpload;
